import { Box, Button, Stack, Tooltip, Typography } from '@mui/joy';
import { motion, useReducedMotion } from 'framer-motion';
import { useContext } from 'react';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import { LayoutContext } from '../../context';

import LogoTitle from '../LogoTitle';

/**
 * Единая шапка экрана: значок бренда, заголовок и кнопки справа.
 *
 * Раньше каждый экран рисовал свой вариант «логотип + h2», из-за чего они
 * разъезжались по отступам и высоте. Теперь один компонент: липнет к верху,
 * подложка размывается при прокрутке контента под ней.
 *
 * contained (по умолчанию) — содержимое шапки выравнивается по той же
 * колонке, что и контент страницы (PageContainer), поэтому на ПК заголовок
 * стоит ровно над карточками. В узких колонках (список чатов) передавайте
 * contained={false}.
 *
 *   <PageHeader title="Лента" actions={<Button>Фильтр</Button>} />
 */
export default function PageHeader({
    title,
    subtitle,
    actions,
    children,
    sticky = true,
    logo = true,
    contained = true,
    width = 'normal',
    suggestion = false,
}) {
    const { setModalSuggestion } = useContext(LayoutContext);
    const reduceMotion = useReducedMotion();
    const mod = width === 'narrow' ? ' lvc-page--narrow' : width === 'wide' ? ' lvc-page--wide' : '';

    const inner = (
        <>
            <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}
            >
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
                    {logo ? <LogoTitle size={34} /> : null}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            level="h2"
                            component="h1"
                            sx={{
                                fontSize: { xs: 22, sm: 26 },
                                lineHeight: 1.15,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {title}
                        </Typography>
                        {subtitle ? (
                            <Typography level="body-xs" sx={{ color: 'var(--lvc-ink-soft)', mt: 0.25 }}>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Box>
                </Stack>

                {(actions || suggestion) ? (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 'none' }}>
                        {suggestion && (
                            <Tooltip title="Предложить улучшение" placement="bottom">
                                <Button
                                    variant="soft"
                                    color="primary"
                                    aria-label="Предложить улучшение"
                                    onClick={() => setModalSuggestion({ open: true })}
                                    sx={{
                                        minWidth: 40, minHeight: 40, px: { xs: 1, sm: 1.5 },
                                        borderRadius: '14px', gap: 0.75,
                                        background: 'var(--lvc-grad-soft)', color: '#7a3ae8',
                                        border: '1px solid rgba(138, 77, 255, .12)',
                                        fontSize: 12, whiteSpace: 'nowrap',
                                        '&:hover': { background: '#eee3ff', boxShadow: '0 4px 16px rgba(138, 77, 255, .12)' },
                                    }}
                                >
                                    <LightbulbRoundedIcon sx={{ fontSize: 20 }} />
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Предложить идею</Box>
                                </Button>
                            </Tooltip>
                        )}
                        {actions}
                    </Stack>
                ) : null}
            </Stack>

            {children ? <Box sx={{ mt: 1.25 }}>{children}</Box> : null}
        </>
    );

    return (
        <Box
            component={motion.header}
            initial={reduceMotion ? false : { opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
            sx={{
                position: sticky ? 'sticky' : 'static',
                top: 0,
                zIndex: 20,
                px: contained ? 0 : { xs: 2, sm: 2.5 },
                pt: { xs: 1.5, sm: 2 },
                pb: 1.25,
                backgroundColor: 'rgba(255,255,255,0.78)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderBottom: '1px solid',
                borderColor: 'var(--lvc-line)',
            }}
        >
            {contained ? <div className={`lvc-page${mod}`}>{inner}</div> : inner}
        </Box>
    );
}
